import React, { useState, useMemo } from 'react';
import { Calculator, Package, Monitor, CreditCard, Tablet, Printer, Check, FileDown, Sparkles, Eye, X, Mail, Globe, ShoppingCart, Smartphone, ScreenShare, Users, CalendarDays, Bell, MailOpen, Truck, FileText, QrCode, BarChart3, Wrench, Settings, Rocket, Tv, MonitorSmartphone, TouchpadIcon } from 'lucide-react';
import './Simulateur.css';

const MODULES = [
  { id: 'site-web', nom: 'Site web professionnel', icon: <Globe size={22} />, mensuel: 49, setup: 425, prixFixe: 2500 },
  { id: 'ecommerce', nom: 'E-commerce complet', icon: <ShoppingCart size={22} />, mensuel: 79, setup: 425, prixFixe: 3900 },
  { id: 'pwa', nom: 'Application mobile PWA', icon: <Smartphone size={22} />, mensuel: 189, setup: 150, prixFixe: 4900 },
  { id: 'borne', nom: 'Borne de commande', icon: <ScreenShare size={22} />, mensuel: 39, setup: 60, prixFixe: 1900 },
  { id: 'fidelite', nom: 'Carte de fidélité digitale', icon: <CreditCard size={22} />, mensuel: 59, setup: 0, prixFixe: 0 },
  { id: 'crm', nom: 'CRM & Gestion clients', icon: <Users size={22} />, mensuel: 89, setup: 0, prixFixe: 0 },
  { id: 'reservation', nom: 'Réservation & Agenda', icon: <CalendarDays size={22} />, mensuel: 79, setup: 0, prixFixe: 0 },
  { id: 'notifications', nom: 'Notifications push illimitées', icon: <Bell size={22} />, mensuel: 149, setup: 0, prixFixe: 0 },
  { id: 'emailing', nom: 'Emailing & SMS marketing', icon: <MailOpen size={22} />, mensuel: 89, setup: 0, prixFixe: 0 },
  { id: 'click-collect', nom: 'Click & Collect / Livraison', icon: <Truck size={22} />, mensuel: 49, setup: 0, prixFixe: 0 },
  { id: 'devis-factures', nom: 'Devis & Factures', icon: <FileText size={22} />, mensuel: 29, setup: 0, prixFixe: 0 },
  { id: 'qr-codes', nom: 'QR Codes & Menus digitaux', icon: <QrCode size={22} />, mensuel: 19, setup: 0, prixFixe: 0 },
  { id: 'stats', nom: 'Statistiques avancées', icon: <BarChart3 size={22} />, mensuel: 29, setup: 0, prixFixe: 0 },
];

const PACK_PREMIUM = { mensuel: 599, setup: 1050, prixFixe: 11900 };
const TOTAL_INDIVIDUEL_MENSUEL = MODULES.reduce((s, m) => s + m.mensuel, 0);
const TOTAL_INDIVIDUEL_FIXE = MODULES.reduce((s, m) => s + m.prixFixe, 0);

const MAINTENANCES = [
  {
    id: 'basique',
    nom: 'Maintenance Basique',
    icon: <Wrench size={20} />,
    mensuel: 59,
    badge: null,
    description: 'Support essentiel pour garder votre site en ligne et à jour.',
    features: [
      'Mises à jour de sécurité',
      'Sauvegarde hebdomadaire',
      'Support par email (48h)',
      'Monitoring disponibilité',
    ],
  },
  {
    id: 'standard',
    nom: 'Maintenance Standard',
    icon: <Settings size={20} />,
    mensuel: 149,
    badge: 'Recommandé',
    description: 'Maintenance complète avec support réactif et optimisations régulières.',
    features: [
      'Tout Basique inclus',
      'Sauvegarde quotidienne',
      'Support prioritaire (12h)',
      'Optimisation performances',
      'Modifications mineures incluses',
      'Rapport mensuel',
    ],
  },
  {
    id: 'premium',
    nom: 'Maintenance Premium',
    icon: <Rocket size={20} />,
    mensuel: 299,
    badge: null,
    description: 'Service VIP avec support dédié, évolutions continues et disponibilité maximale.',
    features: [
      'Mises à jour iOS et Android',
      'Correction de bugs',
      'Support dédié 24/7',
      'Modifications illimitées',
      'Évolutions fonctionnelles',
      'Audit trimestriel SEO',
      'Interlocuteur unique',
    ],
  },
];

const MATERIELS = [
  {
    id: 'novodisplay-43',
    nom: 'NovoDisplay 43"',
    sousTitre: 'Affichage',
    icon: <Tv size={20} />,
    prixFixe: 2490,
    mensuel: 99,
    description: 'Écran d\'affichage professionnel pour vitrine et accueil. Diffusez vos menus, promotions et actualités en continu.',
    badge: null,
    features: [
      'Interface Intégrée',
      'Support 48h',
      'Écran 43 pouces Full HD',
      'Affichage dynamique à distance',
      'Installation et formation incluses',
      'Garantie matériel 36 mois',
    ],
  },
  {
    id: 'lenovo-24-tactile',
    nom: 'Lenovo 24" Tactile',
    sousTitre: 'Catalogue Produits',
    icon: <MonitorSmartphone size={20} />,
    prixFixe: 4490,
    mensuel: 199,
    description: 'Écran tactile interactif pour consultation catalogue, prise de commande et navigation client autonome.',
    badge: 'Interactif - Populaire',
    features: [
      'Site + PWA Intégrés',
      'Support 12h',
      'Écran tactile 24 pouces Lenovo',
      'Navigation interactive clients',
      'PWA + catalogue produits intégré',
      'Garantie matériel 36 mois',
    ],
  },
  {
    id: 'novotouch-65',
    nom: 'Borne Pro NovoTouch 65"',
    sousTitre: 'Commander',
    icon: <TouchpadIcon size={20} />,
    prixFixe: 6990,
    mensuel: 299,
    description: 'Borne de commande autonome grand format. Prise de commande, paiement intégré et application native pour vos clients.',
    badge: null,
    features: [
      'Site + App Native Intégrés',
      'Support 24/7',
      'Borne tactile 65 pouces',
      'Prise de commande + paiement CB',
      'Application native iOS & Android',
      'Garantie matériel 36 mois',
    ],
  },
];

const ModuleMockup = ({ type }) => {
  const mockups = {
    'site-web': (
      <div className="mm-laptop">
        <div className="mm-laptop-screen">
          <div className="mm-lb"><span className="mm-dot-r"/><span className="mm-dot-y"/><span className="mm-dot-g"/><div className="mm-url"><span className="mm-lock"/><span className="mm-url-text">www.votre-site.fr</span></div></div>
          <div className="mm-site-body">
            <div className="mm-site-nav"><span/><span/><span/><span className="mm-nav-cta"/></div>
            <div className="mm-site-hero">
              <div className="mm-site-hero-l"><div className="mm-sh-title"/><div className="mm-sh-sub"/><div className="mm-sh-btn"/></div>
              <div className="mm-site-hero-r"><div className="mm-sh-img"><div className="mm-sh-img-shine"/></div></div>
            </div>
            <div className="mm-site-cards"><div className="mm-sc"><div className="mm-sc-ico"/><span/><span/></div><div className="mm-sc"><div className="mm-sc-ico"/><span/><span/></div><div className="mm-sc"><div className="mm-sc-ico"/><span/><span/></div></div>
          </div>
        </div>
        <div className="mm-laptop-base"><div className="mm-laptop-notch"/></div>
        <div className="mm-float-badge mm-fb1">SEO 98/100</div>
      </div>
    ),
    'ecommerce': (
      <div className="mm-laptop">
        <div className="mm-laptop-screen">
          <div className="mm-lb"><span className="mm-dot-r"/><span className="mm-dot-y"/><span className="mm-dot-g"/><div className="mm-url"><span className="mm-lock"/><span className="mm-url-text">shop.votre-site.fr</span></div></div>
          <div className="mm-site-body mm-shop-body">
            <div className="mm-shop-nav"><span/><div className="mm-shop-search"/><div className="mm-shop-cart"><ShoppingCart size={8}/><span className="mm-cart-count">3</span></div></div>
            <div className="mm-shop-grid">
              <div className="mm-shop-item"><div className="mm-si-img mm-si1"/><div className="mm-si-info"><span/><span className="mm-si-price">49€</span></div><div className="mm-si-btn">Ajouter</div></div>
              <div className="mm-shop-item"><div className="mm-si-img mm-si2"/><div className="mm-si-info"><span/><span className="mm-si-price">89€</span></div><div className="mm-si-btn">Ajouter</div></div>
              <div className="mm-shop-item mm-si-hot"><div className="mm-si-tag">-30%</div><div className="mm-si-img mm-si3"/><div className="mm-si-info"><span/><span className="mm-si-price">129€</span></div><div className="mm-si-btn">Ajouter</div></div>
            </div>
          </div>
        </div>
        <div className="mm-laptop-base"><div className="mm-laptop-notch"/></div>
        <div className="mm-float-badge mm-fb2">+24% ventes</div>
      </div>
    ),
    'pwa': (
      <div className="mm-iphone">
        <div className="mm-iphone-btn-l1"/><div className="mm-iphone-btn-l2"/><div className="mm-iphone-btn-r"/>
        <div className="mm-iphone-screen">
          <div className="mm-iphone-island"/>
          <div className="mm-iphone-status"><span>9:41</span><div className="mm-status-icons"><span/><span/><span/></div></div>
          <div className="mm-pwa-header"><span className="mm-pwa-logo"/><span className="mm-pwa-title"/></div>
          <div className="mm-pwa-content">
            <div className="mm-pwa-card"><div className="mm-pwa-card-img"><div className="mm-pwa-card-shine"/></div><div className="mm-pwa-card-text"><span/><span/></div></div>
            <div className="mm-pwa-list"><div className="mm-pwa-row"><span className="mm-pr-dot"/><span/><span className="mm-pr-arrow"/></div><div className="mm-pwa-row"><span className="mm-pr-dot mm-pr-green"/><span/><span className="mm-pr-arrow"/></div><div className="mm-pwa-row"><span className="mm-pr-dot mm-pr-orange"/><span/><span className="mm-pr-arrow"/></div></div>
          </div>
          <div className="mm-pwa-tabs"><div className="mm-pt active"/><div className="mm-pt"/><div className="mm-pt"/><div className="mm-pt"/></div>
        </div>
        <div className="mm-float-badge mm-fb3">Mobile First</div>
      </div>
    ),
    'borne': (
      <div className="mm-kiosk">
        <div className="mm-kiosk-frame">
          <div className="mm-kiosk-cam"/>
          <div className="mm-kiosk-display">
            <div className="mm-k-header"><span/><span>Commander</span></div>
            <div className="mm-k-products"><div className="mm-k-prod"><div className="mm-kp-img"/><span>Menu 1</span><span className="mm-kp-price">9.90€</span></div><div className="mm-k-prod"><div className="mm-kp-img mm-kp2"/><span>Menu 2</span><span className="mm-kp-price">12.90€</span></div><div className="mm-k-prod"><div className="mm-kp-img mm-kp3"/><span>Menu 3</span><span className="mm-kp-price">14.90€</span></div><div className="mm-k-prod"><div className="mm-kp-img mm-kp4"/><span>Menu 4</span><span className="mm-kp-price">11.90€</span></div></div>
            <div className="mm-k-footer"><div className="mm-k-total"><span>Total</span><span>22.80€</span></div><div className="mm-k-pay">Payer</div></div>
            <div className="mm-k-touch"/>
          </div>
        </div>
        <div className="mm-kiosk-stand"/>
        <div className="mm-kiosk-base2"/>
      </div>
    ),
    'fidelite': (
      <div className="mm-fid-scene">
        <div className="mm-fid-card">
          <div className="mm-fid-holo"/>
          <div className="mm-fid-shine"/>
          <div className="mm-fid-top"><span className="mm-fid-logo">E</span><div className="mm-fid-tier"><Sparkles size={8}/><span>GOLD</span></div></div>
          <div className="mm-fid-name">M. DUPONT</div>
          <div className="mm-fid-bottom"><div className="mm-fid-pts"><span className="mm-fid-pts-num">1,250</span><span className="mm-fid-pts-lbl">points</span></div><div className="mm-fid-bar"><div className="mm-fid-bar-fill"/><div className="mm-fid-bar-glow"/></div><div className="mm-fid-stars">{[1,2,3,4,5].map(i=><span key={i} className={i<=4?'mm-star-on':''}/>)}</div></div>
        </div>
        <div className="mm-fid-notif"><Check size={10}/><span>+50 pts</span></div>
      </div>
    ),
    'crm': (
      <div className="mm-laptop">
        <div className="mm-laptop-screen">
          <div className="mm-lb"><span className="mm-dot-r"/><span className="mm-dot-y"/><span className="mm-dot-g"/><div className="mm-url"><span className="mm-lock"/><span className="mm-url-text">crm.e-shops.fr</span></div></div>
          <div className="mm-site-body mm-crm-body">
            <div className="mm-crm-side"><span className="mm-cs-active"/><span/><span/><span/><span/></div>
            <div className="mm-crm-main">
              <div className="mm-crm-stats"><div className="mm-crm-stat"><span className="mm-cst-val">152</span><span className="mm-cst-lbl">Clients</span><span className="mm-cst-trend mm-up">+12%</span></div><div className="mm-crm-stat"><span className="mm-cst-val">48</span><span className="mm-cst-lbl">Ce mois</span><span className="mm-cst-trend mm-up">+24%</span></div><div className="mm-crm-stat"><span className="mm-cst-val">96%</span><span className="mm-cst-lbl">Satisf.</span><span className="mm-cst-trend mm-up">+3%</span></div></div>
              <div className="mm-crm-chart"><div className="mm-crm-bars"><span/><span/><span/><span/><span/><span/><span/></div></div>
            </div>
          </div>
        </div>
        <div className="mm-laptop-base"><div className="mm-laptop-notch"/></div>
      </div>
    ),
    'reservation': (
      <div className="mm-agenda">
        <div className="mm-agenda-inner">
          <div className="mm-ag-head"><span className="mm-ag-month">Mars 2026</span><div className="mm-ag-arrows"><span>&lt;</span><span>&gt;</span></div></div>
          <div className="mm-ag-days"><span>L</span><span>M</span><span>M</span><span>J</span><span>V</span><span>S</span><span>D</span></div>
          <div className="mm-ag-grid">{[24,25,26,27,28,1,2,3,4,5,6,7,8,9,10,11,12,13,14,15,16,17,18,19,20,21,22,23,24,25,26,27,28,29,30].map((d,i)=><span key={i} className={`${i<5?'mm-ag-prev':''}${d===2&&i>=5?'mm-ag-today':''}${(d===5||d===12||d===18)&&i>=5?'mm-ag-rdv':''}`}>{d}</span>)}</div>
          <div className="mm-ag-event"><div className="mm-ag-ev-dot"/><div className="mm-ag-ev-info"><span>RDV 14h30</span><span>M. Martin</span></div><div className="mm-ag-ev-badge">Confirme</div></div>
        </div>
      </div>
    ),
    'notifications': (
      <div className="mm-notif-scene2">
        <div className="mm-notif-phone">
          <div className="mm-np-screen">
            <div className="mm-np-time">9:41</div>
            <div className="mm-np-stack">
              <div className="mm-np-card mm-npc1"><div className="mm-npc-ico mm-npc-green"><Check size={8}/></div><div className="mm-npc-text"><span>Nouvelle commande</span><span>Client #1234 - 299€</span></div><span className="mm-npc-time">2min</span></div>
              <div className="mm-np-card mm-npc2"><div className="mm-npc-ico mm-npc-blue"><Bell size={8}/></div><div className="mm-npc-text"><span>Rappel RDV</span><span>M. Dupont - 14h30</span></div><span className="mm-npc-time">5min</span></div>
              <div className="mm-np-card mm-npc3"><div className="mm-npc-ico mm-npc-purple"><Sparkles size={8}/></div><div className="mm-npc-text"><span>Client fidele</span><span>+150 points gagnes</span></div><span className="mm-npc-time">12min</span></div>
            </div>
          </div>
        </div>
        <div className="mm-notif-ring"/>
      </div>
    ),
    'emailing': (
      <div className="mm-mail-scene">
        <div className="mm-mail-compose">
          <div className="mm-mc-header"><span className="mm-mc-dot"/><span>Nouveau message</span></div>
          <div className="mm-mc-field"><span>A:</span><span className="mm-mc-to">client@entreprise.fr</span></div>
          <div className="mm-mc-field"><span>Objet:</span><span className="mm-mc-obj">Votre offre E-Shops PRO</span></div>
          <div className="mm-mc-body"><span/><span/><span/></div>
          <div className="mm-mc-footer"><div className="mm-mc-send"><Mail size={8}/><span>Envoyer</span></div><div className="mm-mc-attach"/></div>
        </div>
        <div className="mm-mail-flying mm-mf1"/><div className="mm-mail-flying mm-mf2"/><div className="mm-mail-flying mm-mf3"/>
      </div>
    ),
    'click-collect': (
      <div className="mm-cc-scene">
        <div className="mm-cc-map">
          <div className="mm-cc-road"/>
          <div className="mm-cc-truck"><Truck size={14}/></div>
          <div className="mm-cc-pin mm-cc-pin-a"><span>A</span></div>
          <div className="mm-cc-pin mm-cc-pin-b"><span>B</span></div>
        </div>
        <div className="mm-cc-status">
          <div className="mm-cc-step mm-ccs-done"><span>1</span><span>Prepare</span></div>
          <div className="mm-cc-line mm-ccl-done"/>
          <div className="mm-cc-step mm-ccs-done"><span>2</span><span>Expedie</span></div>
          <div className="mm-cc-line mm-ccl-active"/>
          <div className="mm-cc-step mm-ccs-active"><span>3</span><span>En route</span></div>
          <div className="mm-cc-line"/>
          <div className="mm-cc-step"><span>4</span><span>Livre</span></div>
        </div>
      </div>
    ),
    'devis-factures': (
      <div className="mm-invoice-scene">
        <div className="mm-invoice">
          <div className="mm-inv-head"><div className="mm-inv-logo"/><div className="mm-inv-title">FACTURE</div></div>
          <div className="mm-inv-info"><span/><span/></div>
          <div className="mm-inv-table"><div className="mm-inv-row mm-inv-header"><span>Description</span><span>Prix</span></div><div className="mm-inv-row"><span/><span>2 500€</span></div><div className="mm-inv-row"><span/><span>89€/m</span></div><div className="mm-inv-row mm-inv-total"><span>Total TTC</span><span>2 589€</span></div></div>
          <div className="mm-inv-stamp">PAYE</div>
        </div>
        <div className="mm-inv-shadow"/>
      </div>
    ),
    'qr-codes': (
      <div className="mm-qr-scene2">
        <div className="mm-qr-phone">
          <div className="mm-qrp-cam">
            <div className="mm-qrp-corners"><span/><span/><span/><span/></div>
            <div className="mm-qrp-code"><div className="mm-qrp-grid">{Array.from({length:49},(_, i)=><span key={i} className={[0,1,2,3,4,5,6,7,13,14,21,28,35,36,37,38,39,40,41,42,43,44,45,46,47,48].includes(i)?'mm-qd':''}/>)}</div></div>
            <div className="mm-qrp-scan-line"/>
          </div>
        </div>
        <div className="mm-qr-result"><Check size={10}/><span>Menu scanne !</span></div>
      </div>
    ),
    'stats': (
      <div className="mm-stats-scene">
        <div className="mm-stats-dash">
          <div className="mm-sd-kpis"><div className="mm-sd-kpi"><span className="mm-sdk-val">12.4k</span><span className="mm-sdk-lbl">Visites</span><div className="mm-sdk-spark"><span/><span/><span/><span/><span/></div></div><div className="mm-sd-kpi"><span className="mm-sdk-val">3.2%</span><span className="mm-sdk-lbl">Conv.</span><div className="mm-sdk-spark mm-spark-green"><span/><span/><span/><span/><span/></div></div></div>
          <div className="mm-sd-chart">
            <svg viewBox="0 0 120 45" preserveAspectRatio="none" className="mm-sd-svg">
              <defs><linearGradient id="mmG2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#667eea" stopOpacity="0.3"/><stop offset="100%" stopColor="#667eea" stopOpacity="0"/></linearGradient></defs>
              <path d="M0 40 Q10 38 20 32 T40 28 T60 18 T80 22 T100 12 T120 8 V45 H0Z" fill="url(#mmG2)" className="mm-area-anim"/>
              <path d="M0 40 Q10 38 20 32 T40 28 T60 18 T80 22 T100 12 T120 8" fill="none" stroke="#667eea" strokeWidth="2" className="mm-line-anim2"/>
              <circle cx="100" cy="12" r="3" fill="#667eea" className="mm-dot-anim"/>
            </svg>
          </div>
        </div>
      </div>
    ),
  };
  return <div className="module-mockup">{mockups[type]}</div>;
};

const MaterielMockup = ({ type }) => {
  const mockups = {
    'novodisplay-43': (
      /* TV plate - juste l'écran, pas de pied */
      <div className="matm-tv">
        <div className="matm-tv-bezel">
          <div className="matm-tv-screen">
            <div className="matm-tv-bg" />
            <div className="matm-tv-overlay">
              <div className="matm-tv-left">
                <div className="matm-tv-brand">MON RESTO</div>
                <div className="matm-tv-title">Menu du Jour</div>
                <div className="matm-tv-subtitle">Entrée + Plat + Dessert</div>
                <div className="matm-tv-left-bottom">
                  <div className="matm-tv-price-tag">14.90€</div>
                  <div className="matm-tv-qr">
                    <div className="matm-qr-box">
                      <div className="matm-qr-grid">
                        <span /><span /><span /><span /><span />
                        <span /><span /><span /><span /><span />
                        <span /><span /><span /><span /><span />
                        <span /><span /><span /><span /><span />
                        <span /><span /><span /><span /><span />
                      </div>
                      <div className="matm-qr-corner tl" />
                      <div className="matm-qr-corner tr" />
                      <div className="matm-qr-corner bl" />
                    </div>
                    <div className="matm-qr-label">Scannez-moi</div>
                  </div>
                </div>
              </div>
              <div className="matm-tv-right">
                <div className="matm-tv-circle c1" />
                <div className="matm-tv-circle c2" />
                <div className="matm-tv-circle c3" />
                <div className="matm-tv-badge-promo">
                  <span className="matm-tv-badge-txt">-30%</span>
                </div>
              </div>
            </div>
            <div className="matm-tv-ticker">
              <span className="matm-tv-ticker-text">Ouvert 7j/7 — Livraison gratuite dès 25€ — Happy Hour 17h-19h — Carte fidélité x2 points —</span>
            </div>
            <div className="matm-tv-shine" />
          </div>
        </div>
      </div>
    ),
    'lenovo-24-tactile': (
      /* Moniteur de bureau sur pied */
      <div className="matm-desktop">
        <div className="matm-desktop-bezel">
          <div className="matm-desktop-screen">
            <div className="matm-desktop-nav">
              <div className="matm-desktop-dots">
                <span /><span /><span />
              </div>
              <div className="matm-desktop-url">catalogue.shop</div>
            </div>
            <div className="matm-desktop-body">
              <div className="matm-desktop-sidebar">
                <div className="matm-sb-item active" />
                <div className="matm-sb-item" />
                <div className="matm-sb-item" />
                <div className="matm-sb-item" />
              </div>
              <div className="matm-desktop-main">
                <div className="matm-desk-product">
                  <div className="matm-desk-prod-img" />
                  <div className="matm-desk-prod-line" />
                </div>
                <div className="matm-desk-product">
                  <div className="matm-desk-prod-img" style={{background: 'linear-gradient(135deg, #a78bfa, #7c3aed)'}} />
                  <div className="matm-desk-prod-line" />
                </div>
                <div className="matm-desk-product">
                  <div className="matm-desk-prod-img" style={{background: 'linear-gradient(135deg, #f59e0b, #ea580c)'}} />
                  <div className="matm-desk-prod-line" />
                </div>
              </div>
            </div>
            <div className="matm-desktop-touch-ring" />
          </div>
        </div>
        <div className="matm-desktop-neck" />
        <div className="matm-desktop-foot" />
      </div>
    ),
    'novotouch-65': (
      /* Borne verticale (portrait) - plus grande que les autres */
      <div className="matm-borne">
        <div className="matm-borne-body">
          <div className="matm-borne-bezel">
            <div className="matm-borne-screen">
              <div className="matm-borne-header">
                <div className="matm-borne-logo">COMMANDEZ ICI</div>
              </div>
              <div className="matm-borne-cats">
                <div className="matm-borne-cat active">Burgers</div>
                <div className="matm-borne-cat">Pizzas</div>
                <div className="matm-borne-cat">Boissons</div>
              </div>
              <div className="matm-borne-grid">
                <div className="matm-borne-item">
                  <div className="matm-borne-item-img" />
                  <div className="matm-borne-item-price">8.90€</div>
                </div>
                <div className="matm-borne-item">
                  <div className="matm-borne-item-img" style={{background: 'linear-gradient(135deg, #f59e0b, #dc2626)'}} />
                  <div className="matm-borne-item-price">12.50€</div>
                </div>
                <div className="matm-borne-item">
                  <div className="matm-borne-item-img" style={{background: 'linear-gradient(135deg, #22c55e, #059669)'}} />
                  <div className="matm-borne-item-price">6.50€</div>
                </div>
                <div className="matm-borne-item">
                  <div className="matm-borne-item-img" style={{background: 'linear-gradient(135deg, #a78bfa, #7c3aed)'}} />
                  <div className="matm-borne-item-price">9.90€</div>
                </div>
              </div>
              <div className="matm-borne-footer">
                <div className="matm-borne-total">Total: 21.40€</div>
                <div className="matm-borne-pay-btn">Payer</div>
              </div>
              <div className="matm-borne-touch" />
            </div>
          </div>
        </div>
        <div className="matm-borne-pillar" />
        <div className="matm-borne-base" />
      </div>
    ),
  };
  return <div className={`materiel-mockup ${type === 'novotouch-65' ? 'materiel-mockup-tall' : ''}`}>{mockups[type]}</div>;
};

const Simulateur = ({ user, token }) => {
  const [selectedModules, setSelectedModules] = useState([]);
  const [materiels, setMateriels] = useState({});
  const [selectedMaintenance, setSelectedMaintenance] = useState(null);
  const [priseEnCharge, setPriseEnCharge] = useState({
    modules: { active: false, pourcent: 100 },
    maintenance: { active: false, pourcent: 50 },
  });
  const [clientNom, setClientNom] = useState('');
  const [clientEntreprise, setClientEntreprise] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientTelephone, setClientTelephone] = useState('');
  const [clientSiret, setClientSiret] = useState('');
  const [clientAdresse, setClientAdresse] = useState('');
  const [clientCodePostal, setClientCodePostal] = useState('');
  const [clientVille, setClientVille] = useState('');
  const [siretLoading, setSiretLoading] = useState(false);
  const [siretError, setSiretError] = useState('');
  const [generating, setGenerating] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [clientFieldErrors, setClientFieldErrors] = useState({});
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showPartenariatModal, setShowPartenariatModal] = useState(false);
  const [showMaterielRequiredModal, setShowMaterielRequiredModal] = useState(false);
  const [pendingPec, setPendingPec] = useState(null);
  const [partenariatAccepted, setPartenariatAccepted] = useState(false);
  const [engagements, setEngagements] = useState({
    confidentialite: false,
    accompagnement: false,
    visibilite: false,
    temoignage: false,
    etudeCas: false,
    recommandation: false,
    retours: false,
    utilisation: false,
  });

  const allSelected = selectedModules.length === MODULES.length;

  const getAutoMaintenance = (modules) => {
    if (modules.includes('pwa')) return 'premium';
    if (modules.includes('ecommerce')) return 'standard';
    if (modules.length > 0) return 'basique';
    return null;
  };

  const toggleModule = (id) => {
    setSelectedModules(prev => {
      const next = prev.includes(id) ? prev.filter(m => m !== id) : [...prev, id];
      setSelectedMaintenance(getAutoMaintenance(next));
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelectedModules([]);
      setSelectedMaintenance(null);
    } else {
      const all = MODULES.map(m => m.id);
      setSelectedModules(all);
      setSelectedMaintenance(getAutoMaintenance(all));
    }
  };

  const updateMateriel = (id, delta) => {
    setMateriels(prev => {
      const current = prev[id] || 0;
      const next = Math.max(0, current + delta);
      let newMateriels;
      if (next === 0) {
        const copy = { ...prev };
        delete copy[id];
        newMateriels = copy;
      } else {
        newMateriels = { ...prev, [id]: next };
      }
      // Si plus de matériel et maintenance >50%, ramener à 50% (sauf Partenaire Administratif)
      if (Object.keys(newMateriels).length === 0 && priseEnCharge.maintenance.pourcent > 50 && priseEnCharge.maintenance.pourcent !== 101) {
        setPriseEnCharge(prev => ({ ...prev, maintenance: { ...prev.maintenance, pourcent: 50 } }));
      }
      return newMateriels;
    });
  };

  // Price calculations
  const calculs = useMemo(() => {
    const isPack = allSelected;
    const modulesSelected = MODULES.filter(m => selectedModules.includes(m.id));

    let mensuelBrut = isPack ? PACK_PREMIUM.mensuel : modulesSelected.reduce((s, m) => s + m.mensuel, 0);
    let setupBrut;
    if (isPack) {
      setupBrut = 875;
    } else if (modulesSelected.some(m => m.id === 'pwa')) {
      setupBrut = 875;
    } else {
      setupBrut = modulesSelected.reduce((s, m) => s + m.setup, 0);
    }
    let prixFixeBrut = isPack ? PACK_PREMIUM.prixFixe : modulesSelected.reduce((s, m) => s + m.prixFixe, 0);

    // Materiel
    let materielMensuelTotal = 0;
    let materielFixeTotal = 0;
    Object.entries(materiels).forEach(([id, qty]) => {
      const mat = MATERIELS.find(m => m.id === id);
      if (mat) {
        materielMensuelTotal += mat.mensuel * qty;
        materielFixeTotal += mat.prixFixe * qty;
      }
    });

    // Maintenance
    const maintenanceObj = MAINTENANCES.find(m => m.id === selectedMaintenance);
    const maintenanceMensuel = maintenanceObj ? maintenanceObj.mensuel : 0;

    // Total before discount (maintenance séparée)
    const totalSetupBrut = setupBrut;
    const totalMensuelBrut = mensuelBrut + materielMensuelTotal;

    // Prise en charge (avec pourcentage) - modules uniquement (pas matériel)
    const pecModulesPourcent = priseEnCharge.modules.active ? priseEnCharge.modules.pourcent : 0;
    const pecPrixFixe = Math.round(prixFixeBrut * pecModulesPourcent / 100);
    const pecAbonnement = Math.round(mensuelBrut * pecModulesPourcent / 100);
    const pecMaintenancePourcent = Math.min(priseEnCharge.maintenance.pourcent, 100);
    const pecMaintenance = priseEnCharge.maintenance.active ? Math.round(maintenanceMensuel * pecMaintenancePourcent / 100) : 0;
    const totalPriseEnCharge = pecPrixFixe + (pecAbonnement * 36) + (pecMaintenance * 36);

    const prixFixeFinal = prixFixeBrut - pecPrixFixe;
    const mensuelFinal = mensuelBrut - pecAbonnement;
    const setupFinal = totalSetupBrut;
    const maintenanceFinal = maintenanceMensuel - pecMaintenance;

    return {
      isPack,
      modulesSelected,
      mensuelBrut,
      setupBrut,
      prixFixeBrut,
      materielMensuelTotal,
      materielFixeTotal,
      maintenanceMensuel,
      maintenanceObj,
      totalSetupBrut,
      totalMensuelBrut,
      totalPriseEnCharge,
      pecPrixFixe,
      pecAbonnement,
      pecMaintenance,
      prixFixeFinal,
      setupFinal,
      mensuelFinal,
      maintenanceFinal,
      economie: isPack ? TOTAL_INDIVIDUEL_MENSUEL - PACK_PREMIUM.mensuel : 0,
    };
  }, [selectedModules, materiels, selectedMaintenance, priseEnCharge, allSelected]);

  const ENGAGEMENTS_LIST = [
    { id: 'confidentialite', icon: '🔒', tag: 'Confidentiel', titre: 'Confidentialité des Tarifs', desc: 'Les tarifs préférentiels partenaires sont strictement confidentiels. Ne pas divulguer les conditions tarifaires à des tiers ou sur les réseaux sociaux.' },
    { id: 'accompagnement', icon: '🤝', tag: 'Entraide', titre: 'Accompagnement Prospects', desc: 'Accepter de répondre aux questions de futurs clients intéressés par notre solution. Partager votre expérience utilisateur de façon honnête et constructive.' },
    { id: 'visibilite', icon: '🏷️', tag: 'Branding', titre: 'Visibilité de Notre Marque', desc: 'Autoriser l\'affichage discret du logo "Propulsé par E Shops PRO" sur votre application. Un petit encart en pied de page qui valorise notre partenariat.' },
    { id: 'temoignage', icon: '⭐', tag: 'Témoignage', titre: 'Témoignage & Avis', desc: 'Accepter de fournir un témoignage écrit ou vidéo sur votre expérience après 3 mois d\'utilisation. Votre retour aide d\'autres professionnels à nous découvrir.' },
    { id: 'etudeCas', icon: '📋', tag: 'Vitrine', titre: 'Étude de Cas', desc: 'Autoriser l\'utilisation de votre projet comme exemple de réalisation sur notre site et supports commerciaux (captures d\'écran, statistiques anonymisées).' },
    { id: 'recommandation', icon: '📢', tag: 'Réseau', titre: 'Recommandation Active', desc: 'Si vous êtes satisfait, recommander nos services à votre réseau professionnel (commerçants, artisans, restaurateurs). Le bouche-à-oreille est notre meilleur allié.' },
    { id: 'retours', icon: '💬', tag: 'Feedback', titre: 'Retours Constructifs', desc: 'Partager vos suggestions d\'amélioration et signaler les éventuels bugs. Votre feedback nous aide à améliorer continuellement notre solution.' },
    { id: 'utilisation', icon: '🚀', tag: 'Actif', titre: 'Utilisation Active', desc: 'S\'engager à utiliser activement l\'application : envoyer des notifications régulières, mettre à jour le contenu et animer votre communauté client.' },
  ];

  const allEngagementsChecked = Object.values(engagements).every(v => v);

  const handlePecClick = (type, currentlyActive) => {
    if (currentlyActive) {
      // Décocher : pas besoin de popup
      if (type === 'modules') {
        setPriseEnCharge(prev => ({ ...prev, modules: { ...prev.modules, active: false } }));
      } else {
        setPriseEnCharge(prev => ({ ...prev, maintenance: { ...prev.maintenance, active: false } }));
      }
      return;
    }
    if (partenariatAccepted) {
      // Déjà validé : activer directement
      if (type === 'modules') {
        setPriseEnCharge(prev => ({ ...prev, modules: { ...prev.modules, active: true } }));
      } else {
        setPriseEnCharge(prev => ({ ...prev, maintenance: { ...prev.maintenance, active: true } }));
      }
      return;
    }
    // Pas encore validé : ouvrir le popup
    setPendingPec(type);
    setShowPartenariatModal(true);
  };

  const handlePartenariatValidate = () => {
    setPartenariatAccepted(true);
    setShowPartenariatModal(false);
    if (pendingPec === 'modules') {
      setPriseEnCharge(prev => ({ ...prev, modules: { ...prev.modules, active: true } }));
    } else if (pendingPec === 'maintenance') {
      setPriseEnCharge(prev => ({ ...prev, maintenance: { ...prev.maintenance, active: true } }));
    }
    setPendingPec(null);
  };

  const hasMateriel = Object.keys(materiels).length > 0;

  const handleMaintenancePourcentChange = (newPourcent) => {
    if (newPourcent === 101) {
      setPriseEnCharge(prev => ({ ...prev, maintenance: { ...prev.maintenance, pourcent: 101 } }));
      return;
    }
    if (newPourcent > 50 && !hasMateriel) {
      setShowMaterielRequiredModal(true);
      return;
    }
    setPriseEnCharge(prev => ({ ...prev, maintenance: { ...prev.maintenance, pourcent: newPourcent } }));
  };

  const handleSiretLookup = async (siret) => {
    const cleaned = siret.replace(/\s/g, '');
    setClientSiret(siret);
    setSiretError('');
    if (cleaned.length !== 9 && cleaned.length !== 14) {
      return;
    }
    setSiretLoading(true);
    try {
      const resp = await fetch(`https://recherche-entreprises.api.gouv.fr/search?q=${cleaned}`);
      if (resp.ok) {
        const json = await resp.json();
        if (json.results && json.results.length > 0) {
          const r = json.results[0];
          setClientEntreprise(r.nom_complet || r.nom_raison_sociale || '');
          const siege = r.siege || {};
          setClientAdresse(siege.adresse || siege.libelle_voie ? `${siege.numero_voie || ''} ${siege.type_voie || ''} ${siege.libelle_voie || ''}`.trim() : '');
          setClientCodePostal(siege.code_postal || '');
          setClientVille(siege.libelle_commune || '');
          // Remplir le nom du dirigeant
          if (r.dirigeants && r.dirigeants.length > 0) {
            const dir = r.dirigeants[0];
            const prenoms = dir.prenoms || dir.prenom || '';
            const nom = dir.nom || '';
            setClientNom(`${prenoms} ${nom}`.trim());
          }
          setSiretLoading(false);
          return;
        }
      }
      setSiretError(cleaned.length === 9 ? 'SIREN non trouvé' : 'SIRET non trouvé');
    } catch (err) {
      setSiretError('Erreur de recherche');
    }
    setSiretLoading(false);
  };

  const validateClientFields = () => {
    const errors = {};
    if (!clientNom.trim()) errors.nom = true;
    if (!clientEntreprise.trim()) errors.entreprise = true;
    if (!clientEmail.trim()) errors.email = true;
    if (!clientTelephone.trim()) errors.telephone = true;
    if (!clientAdresse.trim()) errors.adresse = true;
    if (!clientCodePostal.trim()) errors.codePostal = true;
    if (!clientVille.trim()) errors.ville = true;
    setClientFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      const missing = [];
      if (errors.nom) missing.push('Nom');
      if (errors.entreprise) missing.push('Entreprise');
      if (errors.email) missing.push('Email');
      if (errors.telephone) missing.push('Téléphone');
      if (errors.adresse) missing.push('Adresse');
      if (errors.codePostal) missing.push('Code postal');
      if (errors.ville) missing.push('Ville');
      alert(`Veuillez remplir tous les champs client obligatoires :\n${missing.join(', ')}`);
      if (!showDetailModal) setShowDetailModal(true);
      return false;
    }
    return true;
  };

  const handleDownloadPdf = async () => {
    if (calculs.modulesSelected.length === 0 && Object.keys(materiels).length === 0) return;
    if (!validateClientFields()) return;
    setGenerating(true);
    try {
      const payload = {
        modules: calculs.modulesSelected.map(m => ({
          nom: m.nom,
          mensuel: calculs.isPack ? null : m.mensuel,
          setup: calculs.isPack ? null : m.setup,
          prixFixe: calculs.isPack ? null : m.prixFixe,
        })),
        isPack: calculs.isPack,
        packMensuel: PACK_PREMIUM.mensuel,
        packSetup: PACK_PREMIUM.setup,
        packPrixFixe: PACK_PREMIUM.prixFixe,
        prixFixeTotal: calculs.prixFixeBrut,
        materiels: Object.entries(materiels).map(([id, qty]) => {
          const mat = MATERIELS.find(m => m.id === id);
          return { nom: mat.nom, prixFixe: mat.prixFixe, mensuel: mat.mensuel, qty, features: mat.features };
        }),
        maintenance: calculs.maintenanceObj ? {
          nom: calculs.maintenanceObj.nom,
          mensuel: calculs.maintenanceObj.mensuel,
          features: calculs.maintenanceObj.features,
        } : null,
        priseEnCharge: {
          prixFixe: priseEnCharge.modules.active ? priseEnCharge.modules.pourcent : 0,
          abonnement: priseEnCharge.modules.active ? priseEnCharge.modules.pourcent : 0,
          maintenance: priseEnCharge.maintenance.active ? Math.min(priseEnCharge.maintenance.pourcent, 100) : 0,
          fraisDossier: 0,
          totalPriseEnCharge: calculs.totalPriseEnCharge,
        },
        totaux: {
          prixFixeBrut: calculs.prixFixeBrut,
          setupBrut: calculs.totalSetupBrut,
          mensuelBrut: calculs.mensuelBrut,
          materielMensuelTotal: calculs.materielMensuelTotal,
          prixFixeFinal: calculs.prixFixeFinal,
          setupFinal: calculs.setupFinal,
          mensuelFinal: calculs.mensuelFinal,
          maintenanceFinal: calculs.maintenanceFinal,
        },
        client: {
          nom: clientNom,
          entreprise: clientEntreprise,
          email: clientEmail,
          telephone: clientTelephone,
          siret: clientSiret.replace(/\s/g, ''),
          adresse: clientAdresse,
          codePostal: clientCodePostal,
          ville: clientVille,
        },
        commercial: user?.nom || 'Commercial',
      };

      const response = await fetch('/api/simulateur/pdf', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error('Erreur PDF');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Simulateur_${clientEntreprise || clientNom || 'offre'}_${new Date().toLocaleDateString('fr-FR').replace(/\//g, '-')}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erreur génération PDF:', err);
      alert('Erreur lors de la génération du PDF');
    } finally {
      setGenerating(false);
    }
  };

  const handleSendEmail = async () => {
    if (calculs.modulesSelected.length === 0 && Object.keys(materiels).length === 0) return;
    if (!validateClientFields()) return;
    setSendingEmail(true);
    try {
      const payload = {
        modules: calculs.modulesSelected.map(m => ({
          nom: m.nom,
          mensuel: calculs.isPack ? null : m.mensuel,
          setup: calculs.isPack ? null : m.setup,
          prixFixe: calculs.isPack ? null : m.prixFixe,
        })),
        isPack: calculs.isPack,
        packMensuel: PACK_PREMIUM.mensuel,
        packSetup: PACK_PREMIUM.setup,
        packPrixFixe: PACK_PREMIUM.prixFixe,
        prixFixeTotal: calculs.prixFixeBrut,
        materiels: Object.entries(materiels).map(([id, qty]) => {
          const mat = MATERIELS.find(m => m.id === id);
          return { nom: mat.nom, prixFixe: mat.prixFixe, mensuel: mat.mensuel, qty, features: mat.features };
        }),
        maintenance: calculs.maintenanceObj ? {
          nom: calculs.maintenanceObj.nom,
          mensuel: calculs.maintenanceObj.mensuel,
          features: calculs.maintenanceObj.features,
        } : null,
        priseEnCharge: {
          prixFixe: priseEnCharge.modules.active ? priseEnCharge.modules.pourcent : 0,
          abonnement: priseEnCharge.modules.active ? priseEnCharge.modules.pourcent : 0,
          maintenance: priseEnCharge.maintenance.active ? Math.min(priseEnCharge.maintenance.pourcent, 100) : 0,
          fraisDossier: 0,
          totalPriseEnCharge: calculs.totalPriseEnCharge,
        },
        totaux: {
          prixFixeBrut: calculs.prixFixeBrut,
          setupBrut: calculs.totalSetupBrut,
          mensuelBrut: calculs.mensuelBrut,
          materielMensuelTotal: calculs.materielMensuelTotal,
          prixFixeFinal: calculs.prixFixeFinal,
          setupFinal: calculs.setupFinal,
          mensuelFinal: calculs.mensuelFinal,
          maintenanceFinal: calculs.maintenanceFinal,
        },
        client: {
          nom: clientNom,
          entreprise: clientEntreprise,
          email: clientEmail,
          telephone: clientTelephone,
          siret: clientSiret.replace(/\s/g, ''),
          adresse: clientAdresse,
          codePostal: clientCodePostal,
          ville: clientVille,
        },
        commercial: user?.nom || 'Commercial',
      };

      const response = await fetch('/api/simulateur/pdf/email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Erreur envoi');
      alert(`Email envoyé avec succès à ${clientEmail}`);
    } catch (err) {
      console.error('Erreur envoi email:', err);
      alert('Erreur lors de l\'envoi de l\'email');
    } finally {
      setSendingEmail(false);
    }
  };

  return (
    <div className="simulateur-wrapper">
      <h2 className="simulateur-title">
        <img src="/logo-eshops-pro.png" alt="E-Shops PRO" className="simulateur-logo" /> Tarifs
      </h2>

      <div className="simulateur-layout">
        {/* LEFT COLUMN - Catalogue */}
        <div className="simulateur-catalogue">
          {/* Pack Premium Banner */}
          <div className={`pack-premium-banner ${allSelected ? 'pack-active' : ''}`}>
            <div className="pack-premium-orb pack-premium-orb-1" />
            <div className="pack-premium-orb pack-premium-orb-2" />
            <div className="pack-premium-orb pack-premium-orb-3" />
            <div className="pack-premium-shine" />

            <div className="pack-premium-content">
              <div className="pack-premium-left">
                <div className="pack-premium-badge">
                  <Sparkles size={14} />
                  <span>OFFRE EXCLUSIVE</span>
                </div>
                <h3 className="pack-premium-title">
                  <Sparkles size={26} className="pack-premium-sparkle" />
                  Pack Premium
                </h3>
                <p className="pack-premium-subtitle">Tous les modules inclus &mdash; La solution digitale complete</p>

                <div className="pack-premium-price-row">
                  <div className="pack-premium-price">
                    <span className="pack-premium-amount">{PACK_PREMIUM.mensuel}</span>
                    <div className="pack-premium-currency">
                      <span className="pack-premium-euro">€</span>
                      <span className="pack-premium-period">/mois</span>
                    </div>
                  </div>
                  <div className="pack-premium-price-details">
                    <span className="pack-premium-engagement">Engagement 36 mois</span>
                    <span className="pack-premium-alt">ou {PACK_PREMIUM.prixFixe.toLocaleString('fr-FR')}€ en une fois + 875€ HT</span>
                  </div>
                </div>

                {allSelected && (
                  <div className="pack-economy">
                    <Sparkles size={14} />
                    Economie de {TOTAL_INDIVIDUEL_MENSUEL - PACK_PREMIUM.mensuel}€/mois soit {((TOTAL_INDIVIDUEL_MENSUEL - PACK_PREMIUM.mensuel) * 36).toLocaleString('fr-FR')}€ sur 36 mois
                  </div>
                )}
              </div>

              <div className="pack-premium-right">
                <div className="pack-premium-features">
                  <div className="pack-premium-feature"><Check size={16} /> 13 modules inclus</div>
                  <div className="pack-premium-feature"><Check size={16} /> Support prioritaire</div>
                  <div className="pack-premium-feature"><Check size={16} /> Mises a jour incluses</div>
                </div>
                <button className={`btn-select-all ${allSelected ? 'btn-active' : ''}`} onClick={toggleAll}>
                  {allSelected ? (
                    <><Check size={18} /> Selectionne</>
                  ) : (
                    <><Sparkles size={18} /> Tout selectionner</>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Modules Grid */}
          <div className="simulateur-section">
            <h3 className="simulateur-section-title">
              <Package size={20} /> Modules ({selectedModules.length}/{MODULES.length})
            </h3>
            <div className="modules-grid">
              {MODULES.map(mod => {
                const isSelected = selectedModules.includes(mod.id);
                return (
                  <div
                    key={mod.id}
                    className={`module-card ${isSelected ? 'selected' : ''}`}
                    onClick={() => toggleModule(mod.id)}
                  >
                    <div className="module-card-glow" />
                    <div className="module-card-inner">
                      <ModuleMockup type={mod.id} />
                      <div className="module-card-header">
                        <div className="module-card-icon-wrap">
                          <span className="module-card-icon">{mod.icon}</span>
                        </div>
                        <div className="module-checkbox">
                          {isSelected && <Check size={14} color="white" />}
                        </div>
                      </div>
                      <div className="module-card-name">{mod.nom}</div>
                      <div className="module-card-divider" />
                      {mod.prixFixe > 0 ? (
                        <div className="module-card-pricing">
                          <div className="module-card-prix-fixe">{mod.prixFixe.toLocaleString('fr-FR')}€</div>
                          <div className="module-card-alt">ou {mod.mensuel}€/mois x36 mois{mod.id === 'pwa' ? ' + 875€ HT frais de dossier' : mod.setup > 0 ? ` + ${mod.setup}€ HT frais de dossier` : ''}</div>
                        </div>
                      ) : (
                        <div className="module-card-pricing">
                          <div className="module-card-prix-fixe">{mod.mensuel}€<span className="module-card-prix-suffix">/mois</span></div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Materiel Grid */}
          <div className="simulateur-section">
            <h3 className="simulateur-section-title">
              <Monitor size={20} /> Matériel
            </h3>
            <div className="materiel-grid">
              {MATERIELS.map(mat => {
                const qty = materiels[mat.id] || 0;
                return (
                  <div key={mat.id} className={`materiel-card ${qty > 0 ? 'has-qty' : ''}`}>
                    <div className="materiel-card-glow" />
                    <div className="materiel-card-inner">
                      {mat.badge && <div className="materiel-badge">{mat.badge}</div>}
                      <MaterielMockup type={mat.id} />
                      <div className="materiel-card-header">
                        <div className="materiel-card-icon-wrap">
                          <span className="materiel-card-icon">{mat.icon}</span>
                        </div>
                        <div className="materiel-card-sous-titre">{mat.sousTitre}</div>
                      </div>
                      <div className="materiel-card-name">{mat.nom}</div>
                      <div className="materiel-card-desc">{mat.description}</div>
                      <div className="materiel-card-divider" />
                      <div className="materiel-card-price-block">
                        <div className="materiel-price-monthly">
                          <span className="materiel-price-month-val">{mat.mensuel}€</span>
                          <span className="materiel-price-month-label">/mois x36</span>
                        </div>
                        <div className="materiel-price-or">ou</div>
                        <div className="materiel-price-main">
                          <span className="materiel-price-amount">{mat.prixFixe.toLocaleString('fr-FR')}€</span>
                          <span className="materiel-price-label">Prix fixe</span>
                        </div>
                      </div>
                      <div className="materiel-card-divider" />
                      <ul className="materiel-features">
                        {mat.features.map((f, i) => (
                          <li key={i}><Check size={14} className="mat-feature-check" /> {f}</li>
                        ))}
                      </ul>
                      <div className="materiel-card-bottom">
                        <div className="qty-control">
                          <button
                            className="qty-btn"
                            onClick={() => updateMateriel(mat.id, -1)}
                            disabled={qty === 0}
                          >
                            −
                          </button>
                          <span className="qty-value">{qty}</span>
                          <button
                            className="qty-btn"
                            onClick={() => updateMateriel(mat.id, 1)}
                          >
                            +
                          </button>
                        </div>
                        {qty === 0 && (
                          <button className="btn-choisir" onClick={() => updateMateriel(mat.id, 1)}>
                            Choisir — {mat.mensuel}€/mois
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Maintenance Grid */}
          {selectedModules.length > 0 && (
          <div className="simulateur-section">
            <h3 className="simulateur-section-title">
              <Wrench size={20} /> Maintenance
            </h3>
            <div className="maintenance-list">
              {MAINTENANCES.map(maint => {
                const isActive = selectedMaintenance === maint.id;
                return (
                  <div
                    key={maint.id}
                    className={`maintenance-row ${isActive ? 'active' : ''}`}
                    onClick={() => setSelectedMaintenance(maint.id)}
                  >
                    <div className={`maintenance-radio ${isActive ? 'checked' : ''}`}>
                      {isActive && <div className="maintenance-radio-dot" />}
                    </div>
                    <span className="maintenance-row-icon">{maint.icon}</span>
                    <div className="maintenance-row-info">
                      <span className="maintenance-row-name">{maint.nom}</span>
                      {maint.badge && <span className="maintenance-row-badge">{maint.badge}</span>}
                    </div>
                    <span className="maintenance-row-price">{maint.mensuel}€<small>/mois</small></span>
                  </div>
                );
              })}
            </div>
          </div>
          )}
        </div>

        {/* RIGHT COLUMN - Recap */}
        <div className="recap-sidebar">
          <div className="recap-card">
            <div className="recap-card-glow" />
            <div className="recap-card-inner">
              <div className="recap-header">
                <div className="recap-header-icon">
                  <Calculator size={20} />
                </div>
                <div className="recap-header-text">
                  <h3>Récapitulatif</h3>
                  <span className="recap-header-sub">Votre offre personnalisée</span>
                </div>
              </div>

              {/* Download PDF - en haut */}
              <button
                className="btn-download-pdf btn-download-pdf-top"
                onClick={handleDownloadPdf}
                disabled={generating || (calculs.modulesSelected.length === 0 && Object.keys(materiels).length === 0)}
              >
                <span className="btn-pdf-shine" />
                <FileDown size={20} />
                {generating ? 'Génération...' : 'Télécharger le PDF'}
              </button>

              {/* Total Box - en haut pour visibilité */}
              <div className="recap-total-box">
                {/* Choix : prix fixe OU abonnement */}
                <div className="recap-total-choice-label">
                  <span className="recap-choice-dot" />
                  Votre offre au choix
                </div>

                {calculs.prixFixeBrut > 0 && (
                  <div className={`recap-total-option ${priseEnCharge.modules.active ? 'pec-active' : ''}`}>
                    <div className="recap-total-option-tag">OPTION 1</div>
                    <div className="recap-total-option-row">
                      <span>Prix fixe</span>
                      {priseEnCharge.modules.active ? (
                        <span className="recap-total-option-value">
                          <span className="prix-barre">{calculs.prixFixeBrut.toLocaleString('fr-FR')}€</span>
                          {calculs.prixFixeFinal === 0 ? (
                            <span className="prix-pec">OFFERT</span>
                          ) : (
                            <span className="prix-reduit">{calculs.prixFixeFinal.toLocaleString('fr-FR')}€</span>
                          )}
                        </span>
                      ) : (
                        <span className="recap-total-option-value">{calculs.prixFixeBrut.toLocaleString('fr-FR')}€</span>
                      )}
                    </div>
                  </div>
                )}

                {calculs.prixFixeBrut > 0 && (
                  <div className="recap-total-ou">OU</div>
                )}

                <div className={`recap-total-option ${priseEnCharge.modules.active ? 'pec-active' : ''}`}>
                  <div className="recap-total-option-tag">{calculs.prixFixeBrut > 0 ? 'OPTION 2' : ''}</div>
                  <div className="recap-total-option-row">
                    <span>Abonnement 36 mois</span>
                    {priseEnCharge.modules.active ? (
                      <span className="recap-total-option-value">
                        <span className="prix-barre">{calculs.mensuelBrut.toLocaleString('fr-FR')}€/mois</span>
                        {calculs.mensuelFinal === 0 ? (
                          <span className="prix-pec">OFFERT</span>
                        ) : (
                          <span className="prix-reduit">{calculs.mensuelFinal.toLocaleString('fr-FR')}€/mois</span>
                        )}
                      </span>
                    ) : (
                      <span className="recap-total-option-value">{calculs.mensuelBrut.toLocaleString('fr-FR')}€/mois</span>
                    )}
                  </div>
                </div>

                {/* Matériel séparé */}
                {calculs.materielMensuelTotal > 0 && (
                  <div className="recap-total-materiel">
                    <div className="recap-total-separator-line"><span>Matériel</span></div>
                    <div className="recap-total-obligatoire-row">
                      <span>Matériel</span>
                      <span>{calculs.materielMensuelTotal.toLocaleString('fr-FR')}€/mois</span>
                    </div>
                  </div>
                )}

                {/* Frais obligatoires */}
                {(calculs.setupBrut > 0 || calculs.maintenanceObj) && (
                  <div className="recap-total-obligatoire">
                    <div className="recap-total-obligatoire-label">Inclus dans tous les cas</div>
                    {calculs.setupBrut > 0 && (
                      <div className="recap-total-obligatoire-row">
                        <span>Frais de dossier</span>
                        <span>{calculs.setupBrut.toLocaleString('fr-FR')}€ HT</span>
                      </div>
                    )}
                    {calculs.maintenanceObj && (
                      <div className={`recap-total-obligatoire-row ${priseEnCharge.maintenance.active ? 'pec-active' : ''}`}>
                        <span>{calculs.maintenanceObj.nom}</span>
                        {priseEnCharge.maintenance.active ? (
                          <span>
                            <span className="prix-barre">{calculs.maintenanceObj.mensuel}€/mois</span>
                            {calculs.maintenanceFinal === 0 ? (
                              <span className="prix-pec">OFFERT</span>
                            ) : (
                              <span className="prix-reduit">{calculs.maintenanceFinal}€/mois</span>
                            )}
                          </span>
                        ) : (
                          <span>{calculs.maintenanceObj.mensuel}€/mois</span>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {calculs.totalPriseEnCharge > 0 && (
                  <div className="recap-total-pec-resume">
                    Prise en charge : {calculs.totalPriseEnCharge.toLocaleString('fr-FR')}€
                  </div>
                )}
              </div>

              {/* Bouton voir détails */}
              <button className="btn-voir-details" onClick={() => setShowDetailModal(true)}>
                <Eye size={18} />
                Voir détails
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* MODAL DÉTAILS PLEIN ÉCRAN */}
      {showDetailModal && (
        <div className="detail-modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div className="detail-modal" onClick={e => e.stopPropagation()}>
            <div className="detail-modal-header">
              <h2>Récapitulatif détaillé</h2>
              <button className="detail-modal-close" onClick={() => setShowDetailModal(false)}>
                <X size={24} />
              </button>
            </div>
            <div className="detail-modal-body">
              <div className="detail-modal-grid">

                {/* COLONNE GAUCHE : Modules + Matériel */}
                <div className="detail-modal-col">
                  {/* Modules */}
                  <div className="detail-section">
                    <div className="detail-section-title">
                      Modules sélectionnés ({calculs.modulesSelected.length})
                    </div>
                    {calculs.modulesSelected.length === 0 ? (
                      <div className="detail-empty">Aucun module sélectionné</div>
                    ) : (
                      <>
                        {calculs.isPack ? (
                          <div className="detail-pack-badge">
                            Pack Premium ({MODULES.length} modules)
                          </div>
                        ) : (
                          calculs.modulesSelected.map(m => (
                            <div key={m.id} className="detail-item">
                              <span>{m.nom}</span>
                              <span className="detail-item-price">
                                {m.prixFixe > 0 ? `${m.prixFixe.toLocaleString('fr-FR')}€` : `${m.mensuel}€/mois`}
                              </span>
                            </div>
                          ))
                        )}
                        <div className="detail-subtotal">
                          {calculs.prixFixeBrut > 0 && (
                            <div className="detail-subtotal-row">
                              <span>Prix fixe modules</span>
                              <span>{calculs.prixFixeBrut.toLocaleString('fr-FR')}€</span>
                            </div>
                          )}
                          <div className="detail-subtotal-row">
                            <span>{calculs.prixFixeBrut > 0 ? 'ou Abonnement 36 mois' : 'Abonnement 36 mois'}</span>
                            <span>{calculs.mensuelBrut}€/mois</span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Maintenance */}
                  {calculs.maintenanceObj && (
                    <div className="detail-section">
                      <div className="detail-section-title">Maintenance</div>
                      <div className="detail-item">
                        <span>{calculs.maintenanceObj.nom}</span>
                        <span className="detail-item-price">{calculs.maintenanceObj.mensuel}€/mois</span>
                      </div>
                      <div className="detail-setup-includes">
                        {calculs.maintenanceObj.features.map((f, i) => (
                          <span key={i}>{f}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Frais de dossier */}
                  {calculs.setupBrut > 0 && (
                    <div className="detail-section">
                      <div className="detail-section-title">Frais de dossier</div>
                      <div className="detail-item">
                        <span>Frais de dossier</span>
                        <span className="detail-item-price">{calculs.setupBrut}€ HT</span>
                      </div>
                      <div className="detail-item" style={{ opacity: 0.8 }}>
                        <span>soit sur 36 mois</span>
                        <span className="detail-item-price">{(calculs.setupBrut / 36).toFixed(2).replace('.', ',')}€/mois</span>
                      </div>
                      <div className="detail-setup-includes">
                        <span>Nom de domaine</span>
                        <span>Compte et licence</span>
                        <span>Hébergement</span>
                      </div>
                    </div>
                  )}

                  {/* Matériel */}
                  <div className="detail-section">
                    <div className="detail-section-title">Matériel</div>
                    {Object.keys(materiels).length === 0 ? (
                      <div className="detail-empty">Aucun matériel ajouté</div>
                    ) : (
                      <>
                        {Object.entries(materiels).map(([id, qty]) => {
                          const mat = MATERIELS.find(m => m.id === id);
                          return (
                            <div key={id} className="detail-item">
                              <span>{qty}x {mat.nom}</span>
                              <span className="detail-item-price">{(mat.mensuel * qty).toLocaleString('fr-FR')}€/mois</span>
                            </div>
                          );
                        })}
                        <div className="detail-subtotal">
                          <div className="detail-subtotal-row">
                            <span>Sous-total matériel</span>
                            <span>{calculs.materielMensuelTotal.toLocaleString('fr-FR')}€/mois</span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* COLONNE DROITE : Infos client + Prise en charge + Total */}
                <div className="detail-modal-col">
                  {/* Infos client */}
                  <div className="detail-section">
                    <div className="detail-section-title">Infos client <span className="detail-required-note">* champs obligatoires</span></div>
                    <div className="detail-client-inputs">
                      <div className="siret-input-wrapper">
                        <input
                          type="text"
                          placeholder="N° SIREN ou SIRET (auto-remplissage)"
                          value={clientSiret}
                          onChange={e => handleSiretLookup(e.target.value)}
                          maxLength={17}
                          className={siretError ? 'siret-error' : siretLoading ? 'siret-loading' : (clientSiret.replace(/\s/g, '').length === 9 || clientSiret.replace(/\s/g, '').length === 14) && !siretError ? 'siret-success' : ''}
                        />
                        {siretLoading && <div className="siret-spinner" />}
                        {!siretLoading && (clientSiret.replace(/\s/g, '').length === 9 || clientSiret.replace(/\s/g, '').length === 14) && !siretError && clientEntreprise && <div className="siret-check">✓</div>}
                        {siretError && <div className="siret-error-msg">{siretError}</div>}
                      </div>
                      <input
                        type="text"
                        placeholder="Nom du client *"
                        value={clientNom}
                        onChange={e => { setClientNom(e.target.value); setClientFieldErrors(prev => ({ ...prev, nom: false })); }}
                        className={clientFieldErrors.nom ? 'input-error' : ''}
                      />
                      <input
                        type="text"
                        placeholder="Entreprise *"
                        value={clientEntreprise}
                        onChange={e => { setClientEntreprise(e.target.value); setClientFieldErrors(prev => ({ ...prev, entreprise: false })); }}
                        className={clientFieldErrors.entreprise ? 'input-error' : ''}
                      />
                      <input
                        type="email"
                        placeholder="Email *"
                        value={clientEmail}
                        onChange={e => { setClientEmail(e.target.value); setClientFieldErrors(prev => ({ ...prev, email: false })); }}
                        className={clientFieldErrors.email ? 'input-error' : ''}
                      />
                      <input
                        type="tel"
                        placeholder="Téléphone *"
                        value={clientTelephone}
                        onChange={e => { setClientTelephone(e.target.value); setClientFieldErrors(prev => ({ ...prev, telephone: false })); }}
                        className={clientFieldErrors.telephone ? 'input-error' : ''}
                      />
                      <div className="detail-client-address-title">Adresse du siège *</div>
                      <input
                        type="text"
                        placeholder="Adresse *"
                        value={clientAdresse}
                        onChange={e => { setClientAdresse(e.target.value); setClientFieldErrors(prev => ({ ...prev, adresse: false })); }}
                        className={clientFieldErrors.adresse ? 'input-error' : ''}
                      />
                      <div className="detail-client-row">
                        <input
                          type="text"
                          placeholder="Code postal *"
                          value={clientCodePostal}
                          onChange={e => { setClientCodePostal(e.target.value); setClientFieldErrors(prev => ({ ...prev, codePostal: false })); }}
                          className={clientFieldErrors.codePostal ? 'input-error' : ''}
                        />
                        <input
                          type="text"
                          placeholder="Ville *"
                          value={clientVille}
                          onChange={e => { setClientVille(e.target.value); setClientFieldErrors(prev => ({ ...prev, ville: false })); }}
                          className={clientFieldErrors.ville ? 'input-error' : ''}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Prise en charge */}
                  <div className="detail-section">
                    <div className="detail-section-title">Prise en charge</div>
                    <div className="pec-checkboxes">
                      <div className={`pec-item ${priseEnCharge.modules.active ? 'pec-checked' : ''}`}>
                        <div className="pec-row" onClick={() => handlePecClick('modules', priseEnCharge.modules.active)}>
                          <div className={`pec-checkbox ${priseEnCharge.modules.active ? 'pec-checkbox-checked' : ''}`}>
                            {priseEnCharge.modules.active && <span>✓</span>}
                          </div>
                          <span className="pec-label">Modules (prix fixe + abonnement)</span>
                        </div>
                        {priseEnCharge.modules.active && (
                          <div className="pec-percent-row">
                            <select
                              className="pec-percent-select"
                              value={priseEnCharge.modules.pourcent}
                              onChange={e => setPriseEnCharge(prev => ({ ...prev, modules: { ...prev.modules, pourcent: parseInt(e.target.value) } }))}
                            >
                              <option value={25}>Découverte</option>
                              <option value={50}>Avantage</option>
                              <option value={75}>Privilège</option>
                              <option value={100}>Intégral</option>
                            </select>
                            <span className="pec-percent-result">-{calculs.pecPrixFixe.toLocaleString('fr-FR')}€ fixe / -{calculs.pecAbonnement.toLocaleString('fr-FR')}€/mois</span>
                          </div>
                        )}
                      </div>
                      {calculs.maintenanceObj && (
                        <div className={`pec-item ${priseEnCharge.maintenance.active ? 'pec-checked' : ''}`}>
                          <div className="pec-row" onClick={() => handlePecClick('maintenance', priseEnCharge.maintenance.active)}>
                            <div className={`pec-checkbox ${priseEnCharge.maintenance.active ? 'pec-checkbox-checked' : ''}`}>
                              {priseEnCharge.maintenance.active && <span>✓</span>}
                            </div>
                            <span className="pec-label">Maintenance</span>
                            <span className="pec-amount">{calculs.maintenanceObj.mensuel}€/mois</span>
                          </div>
                          {priseEnCharge.maintenance.active && (
                            <div className="pec-percent-row">
                              <select
                                className="pec-percent-select"
                                value={priseEnCharge.maintenance.pourcent}
                                onChange={e => handleMaintenancePourcentChange(parseInt(e.target.value))}
                              >
                                <option value={25}>Partenaire</option>
                                <option value={50}>Partenaire confirmé</option>
                                <option value={75}>Partenaire VIP</option>
                                <option value={100}>Partenaire Premium</option>
                                <option value={101}>Partenaire Administratif</option>
                              </select>
                              <span className="pec-percent-result">-{calculs.pecMaintenance.toLocaleString('fr-FR')}€/mois</span>
                            </div>
                          )}
                        </div>
                      )}
                      {calculs.materielMensuelTotal > 0 && (
                        <div className="pec-materiel-note">
                          Matériel non couvert par la prise en charge
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Total dans le modal */}
                  <div className="detail-total-box">
                    <div className="detail-total-header">
                      <div className="detail-total-icon"><svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="2" y="4" width="20" height="16" rx="3" stroke="url(#wallet-grad)" strokeWidth="1.5" fill="url(#wallet-fill)"/><path d="M2 9H22" stroke="rgba(255,255,255,0.2)" strokeWidth="1"/><rect x="15" y="11" width="5" height="4" rx="1" fill="rgba(102,126,234,0.4)" stroke="url(#wallet-grad)" strokeWidth="1"/><circle cx="17.5" cy="13" r="0.8" fill="#43e97b"/><path d="M6 7.5H12" stroke="rgba(255,255,255,0.3)" strokeWidth="1" strokeLinecap="round"/><defs><linearGradient id="wallet-grad" x1="2" y1="4" x2="22" y2="20"><stop stopColor="#667eea"/><stop offset="1" stopColor="#43e97b"/></linearGradient><linearGradient id="wallet-fill" x1="2" y1="4" x2="22" y2="20"><stop stopColor="rgba(102,126,234,0.2)"/><stop offset="1" stopColor="rgba(67,233,123,0.1)"/></linearGradient></defs></svg></div>
                      <div className="detail-total-title">Récapitulatif</div>
                    </div>
                    <div className="detail-total-rows">
                      {calculs.prixFixeBrut > 0 && (
                        <div className="detail-total-row">
                          <span className="detail-total-label">Prix fixe modules</span>
                          <span className="detail-total-value">{priseEnCharge.modules.active ? (
                            <>{calculs.prixFixeFinal === 0 ? <span className="prix-pec">OFFERT</span> : <span className="prix-reduit">{calculs.prixFixeFinal.toLocaleString('fr-FR')}€</span>}</>
                          ) : `${calculs.prixFixeBrut.toLocaleString('fr-FR')}€`}</span>
                        </div>
                      )}
                      <div className="detail-total-row">
                        <span className="detail-total-label">Abonnement modules (36 mois)</span>
                        <span className="detail-total-value">{priseEnCharge.modules.active ? (
                          <>{calculs.mensuelFinal === 0 ? <span className="prix-pec">OFFERT</span> : <span className="prix-reduit">{calculs.mensuelFinal.toLocaleString('fr-FR')}€/mois</span>}</>
                        ) : `${calculs.mensuelBrut.toLocaleString('fr-FR')}€/mois`}</span>
                      </div>
                      {calculs.materielMensuelTotal > 0 && (
                        <div className="detail-total-row">
                          <span className="detail-total-label">Matériel</span>
                          <span className="detail-total-value">{calculs.materielMensuelTotal.toLocaleString('fr-FR')}€/mois</span>
                        </div>
                      )}
                      {calculs.maintenanceObj && (
                        <div className="detail-total-row">
                          <span className="detail-total-label">Maintenance</span>
                          <span className="detail-total-value">{priseEnCharge.maintenance.active ? (
                            <>{calculs.maintenanceFinal === 0 ? <span className="prix-pec">OFFERT</span> : <span className="prix-reduit">{calculs.maintenanceFinal}€/mois</span>}</>
                          ) : `${calculs.maintenanceObj.mensuel}€/mois`}</span>
                        </div>
                      )}
                    </div>
                    {(calculs.materielMensuelTotal > 0 || calculs.maintenanceObj) && (
                      <div className="detail-total-sum">
                        <span>Total mensuel</span>
                        <span>{(calculs.materielMensuelTotal + calculs.maintenanceFinal).toLocaleString('fr-FR')}€/mois</span>
                      </div>
                    )}
                    {calculs.setupBrut > 0 && (
                      <div className="detail-total-row detail-total-row-frais">
                        <span className="detail-total-label">Frais de dossier</span>
                        <span className="detail-total-value">{calculs.setupBrut.toLocaleString('fr-FR')}€ HT</span>
                      </div>
                    )}
                    {calculs.totalPriseEnCharge > 0 && (
                      <div className="detail-total-pec">
                        <span className="detail-total-pec-icon"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2L14 8.5L20 9L15.5 13.5L17 20L12 16.5L7 20L8.5 13.5L4 9L10 8.5L12 2Z" fill="#43e97b" opacity="0.9"/><path d="M5 2L5.7 4.3L8 5L5.7 5.7L5 8L4.3 5.7L2 5L4.3 4.3L5 2Z" fill="#43e97b" opacity="0.6"/><path d="M20 14L20.5 15.5L22 16L20.5 16.5L20 18L19.5 16.5L18 16L19.5 15.5L20 14Z" fill="#43e97b" opacity="0.6"/></svg></span>
                        <span>Prise en charge : -{calculs.totalPriseEnCharge.toLocaleString('fr-FR')}€</span>
                      </div>
                    )}
                  </div>

                  {/* Boutons PDF dans le modal */}
                  <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                    <button
                      className="btn-download-pdf detail-modal-pdf-btn"
                      onClick={handleDownloadPdf}
                      disabled={generating || (calculs.modulesSelected.length === 0 && Object.keys(materiels).length === 0)}
                      style={{ flex: 1 }}
                    >
                      <FileDown size={20} />
                      {generating ? 'Génération...' : 'Télécharger le PDF'}
                    </button>
                    <button
                      className="btn-download-pdf detail-modal-pdf-btn"
                      onClick={handleSendEmail}
                      disabled={sendingEmail || (calculs.modulesSelected.length === 0 && Object.keys(materiels).length === 0)}
                      style={{ flex: 1, background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)' }}
                    >
                      <Mail size={20} />
                      {sendingEmail ? 'Envoi...' : 'Envoyer par email'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* MODAL PARTENARIAT */}
      {showPartenariatModal && (
        <div className="partenariat-overlay" onClick={() => { setShowPartenariatModal(false); setPendingPec(null); }}>
          <div className="partenariat-modal" onClick={e => e.stopPropagation()}>
            <div className="partenariat-header">
              <div>
                <h2>🤝 Partenariat</h2>
                <p className="partenariat-subtitle">Vos Engagements en tant que Partenaire</p>
              </div>
              <button className="partenariat-close" onClick={() => { setShowPartenariatModal(false); setPendingPec(null); }}>
                <X size={22} />
              </button>
            </div>
            <p className="partenariat-intro">
              Pour bénéficier de l'offre partenaire gratuite, quelques engagements simples sont requis
            </p>
            <div className="partenariat-body">
              {ENGAGEMENTS_LIST.map(eng => (
                <label key={eng.id} className={`partenariat-item ${engagements[eng.id] ? 'checked' : ''}`}>
                  <input
                    type="checkbox"
                    checked={engagements[eng.id]}
                    onChange={e => setEngagements(prev => ({ ...prev, [eng.id]: e.target.checked }))}
                  />
                  <div className="partenariat-item-content">
                    <div className="partenariat-item-top">
                      <span className="partenariat-item-icon">{eng.icon}</span>
                      <span className="partenariat-item-titre">{eng.titre}</span>
                      <span className="partenariat-item-tag">{eng.tag}</span>
                    </div>
                    <p className="partenariat-item-desc">{eng.desc}</p>
                  </div>
                </label>
              ))}
            </div>
            <div className="partenariat-footer">
              <div className="partenariat-progress">
                {Object.values(engagements).filter(v => v).length} / {ENGAGEMENTS_LIST.length} engagements acceptés
              </div>
              <button
                className="partenariat-validate"
                disabled={!allEngagementsChecked}
                onClick={handlePartenariatValidate}
              >
                {allEngagementsChecked ? '✅ Valider et activer la prise en charge' : 'Veuillez accepter tous les engagements'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL MATÉRIEL REQUIS */}
      {showMaterielRequiredModal && (
        <div className="partenariat-overlay" onClick={() => setShowMaterielRequiredModal(false)}>
          <div className="materiel-required-modal" onClick={e => e.stopPropagation()}>
            <div className="partenariat-header">
              <div>
                <h2>⚠️ Matériel requis</h2>
              </div>
              <button className="partenariat-close" onClick={() => setShowMaterielRequiredModal(false)}>
                <X size={22} />
              </button>
            </div>
            <p className="materiel-required-msg">
              La prise en charge de plus de 50% de la maintenance est uniquement disponible pour nos partenaires qui bénéficient de matériel.
            </p>
            <div className="materiel-required-body">
              <div className="materiel-required-title">Sélectionnez du matériel :</div>
              <div className="materiel-required-grid">
                {MATERIELS.map(mat => {
                  const qty = materiels[mat.id] || 0;
                  return (
                    <div key={mat.id} className={`materiel-required-card ${qty > 0 ? 'selected' : ''}`}>
                      <div className="materiel-required-card-top">
                        <span className="materiel-required-card-icon">{mat.icon}</span>
                        <span className="materiel-required-card-name">{mat.nom}</span>
                      </div>
                      <div className="materiel-required-card-price">{mat.mensuel}€/mois</div>
                      <div className="materiel-required-card-qty">
                        <button onClick={() => updateMateriel(mat.id, -1)} disabled={qty === 0}>−</button>
                        <span>{qty}</span>
                        <button onClick={() => updateMateriel(mat.id, 1)}>+</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="partenariat-footer">
              <button
                className="partenariat-validate"
                disabled={Object.keys(materiels).length === 0}
                onClick={() => {
                  setShowMaterielRequiredModal(false);
                  setPriseEnCharge(prev => ({ ...prev, maintenance: { ...prev.maintenance, pourcent: 75 } }));
                }}
              >
                {Object.keys(materiels).length > 0 ? '✅ Valider et débloquer +50%' : 'Ajoutez du matériel pour continuer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Simulateur;
